import type {
  M365MapSource,
  M365SyncOutcome,
  M365Tenant,
  M365TenantMappingResult,
} from '@techgitter/contracts';

/**
 * Customer ↔ Microsoft 365 tenant map (requirements §11.5).
 *
 * Reads come from sm4rt-api over the `cr001_m365_tenants` mirror; the mapping
 * write is forwarded by sm4rt-api to sm4rt-cipp-integration, which owns that
 * table. Nothing here talks to the CIPP service directly — like every other
 * page, this one only knows `/api`.
 */

const BASE_URL = '/api/m365/tenants';

async function readJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    let detail = '';
    try {
      const text = await response.text();
      try {
        // sm4rt-api's error envelope, which carries the real reason (unknown
        // tenant, unknown customer, nothing to confirm).
        detail = `: ${JSON.parse(text).error ?? text.slice(0, 200)}`;
      } catch {
        detail = `: ${text.slice(0, 200)}`;
      }
    } catch {
      // ignore
    }
    throw new Error(`${op} failed (${response.status} ${response.statusText})${detail}`);
  }
  return (await response.json()) as T;
}

export interface FetchM365TenantsParams {
  mapSource?: M365MapSource;
  outcome?: M365SyncOutcome;
  customerId?: string;
}

function buildQuery(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** The tenant map — every tenant CIPP reports, mapped or not. */
export async function fetchM365Tenants(
  params: FetchM365TenantsParams = {},
  signal?: AbortSignal,
): Promise<M365Tenant[]> {
  const url = `${BASE_URL}${buildQuery({
    mapSource: params.mapSource,
    outcome: params.outcome,
    customerId: params.customerId,
  })}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
  const data = await readJson<{ items: M365Tenant[] }>(response, 'fetchM365Tenants');
  return data.items ?? [];
}

async function patchMapping(
  tenantId: string,
  body: Record<string, unknown>,
): Promise<M365TenantMappingResult> {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(tenantId)}/mapping`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return readJson<M365TenantMappingResult>(response, 'updateM365TenantMapping');
}

/**
 * Bind (or re-bind) a tenant to a customer. Recorded as a `manual` mapping and
 * confirmed, so the nightly reconciler leaves it alone.
 */
export function mapM365Tenant(
  tenantId: string,
  customerId: string,
): Promise<M365TenantMappingResult> {
  return patchMapping(tenantId, { customerId, confirmed: true });
}

/**
 * Clear a tenant's customer. Also confirmed — otherwise the reconciler re-derives
 * the same wrong domain match tonight and binds it straight back.
 */
export function clearM365TenantMapping(tenantId: string): Promise<M365TenantMappingResult> {
  return patchMapping(tenantId, { customerId: null, confirmed: true });
}

/**
 * Lock in an auto-match without changing it.
 *
 * `customerId` is deliberately **absent** from this body, not null: absent means
 * "confirm what's there", null means "clear it". Sending null here would unbind
 * the very mapping being confirmed. It also preserves `mapSource` — a native-
 * domain match that a human blesses is still a native-domain match.
 */
export function confirmM365TenantMapping(tenantId: string): Promise<M365TenantMappingResult> {
  return patchMapping(tenantId, { confirmed: true });
}

/** Hand a tenant back to auto-matching (un-confirm), keeping its current customer. */
export function unconfirmM365TenantMapping(
  tenantId: string,
): Promise<M365TenantMappingResult> {
  return patchMapping(tenantId, { confirmed: false });
}
